import { type FunctionComponent } from "react";
import classnames from "classnames";

import { SelectionsBoardTheme } from "./SelectionsBoard.types";
import type { SelectionsBoardViewModel, SelectionsBoardSectionViewModel } from "./SelectionsBoard.types";
import { OddsSize } from "@ppb/the-wall-common/types";
import { Odds } from "@ppb/the-wall-web/components/bricks/Indicators/Odds/Odds";

import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import { SystemIconName } from "@ppb/the-wall-icons";
import styles from "./SelectionsBoard.web.css";
import { i18n } from "../../helpers/i18n";

export const SelectionsBoard: FunctionComponent<SelectionsBoardViewModel> = ({
  title,
  theme = SelectionsBoardTheme.Blue,
  icon,
  onRemoveSelection,
  children,
}) => {
  const selectionsBoardContainer = classnames(styles.container, {
    [styles.containerWarning]: theme === SelectionsBoardTheme.Yellow,
  });

  const titleContainer = classnames(styles.titleContainer, {
    [styles.titleContainerDefaultColor]: theme === SelectionsBoardTheme.Blue,
    [styles.titleContainerWarningColor]: theme === SelectionsBoardTheme.Yellow,
  });

  const iconClassnames = classnames(styles.icon, {
    [styles.iconWarning]: theme === SelectionsBoardTheme.Yellow,
  });

  const trashIconColor =
    theme === SelectionsBoardTheme.Yellow
      ? "var(--selections-board-warning-section-title-action-icon-colour)"
      : "var(--selections-board-default-section-title-action-icon-colour)";

  return (
    <article className={selectionsBoardContainer}>
      {title && (
        <section className={titleContainer}>
          <div className={styles.selectionBoardTitle}>
            {icon && (
              <span className={iconClassnames}>
                <GenericIcon name={icon} />
              </span>
            )}
            <h3 className={styles.title}>{title}</h3>
          </div>
          {onRemoveSelection && (
            <button
              onClick={onRemoveSelection}
              type="button"
              aria-label={i18n({ key: "I18N.BETSLIP.REMOVE" })}
              className={styles.removeIcon}
            >
              <GenericIcon name={SystemIconName.TRASH} color={trashIconColor} />
            </button>
          )}
        </section>
      )}
      <section>{children}</section>
    </article>
  );
};

export const SelectionsBoardSection: FunctionComponent<SelectionsBoardSectionViewModel> = ({
  title,
  odd,
  theme = SelectionsBoardTheme.Blue,
  children,
}) => {
  const sectionLabelContainer = classnames(styles.selectionsBoardSectionLabelContainer, {
    [styles.sectionLabelContainerWarning]: theme === SelectionsBoardTheme.Yellow,
  });

  const sectionLabel = classnames(styles.selectionsBoardSectionLabel, {
    [styles.sectionLabelWarning]: theme === SelectionsBoardTheme.Yellow,
  });

  return (
    <article className={styles.selectionsBoardSectionContainer}>
      <section className={sectionLabelContainer}>
        <h4 className={sectionLabel}>{title}</h4>
        {!!odd && (
          <div className={styles.selectionsBoardSectionOdd}>
            <Odds size={OddsSize.SMALL} value={odd} />
          </div>
        )}
      </section>
      <div className={styles.selectionsContent}>{children}</div>
    </article>
  );
};
